import React, { useState, useEffect } from "react";
import { useCompany } from "../../context/companyContext";
import {
  Form,
  Input,
  Button,
  Typography,
  message,
  Flex,
  Radio,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  BankOutlined,
  WhatsAppOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import "../landing/LandingPageNew.css"; // Theme variables
import {
  changevalidations,
} from "../../utils/formValidations";
import { handleBeforeInput } from "../../utils/validationFunctions";
import axiosconfig from "../../config/axiosConfig";
import PhoneInput from "react-phone-input-2"; // Import the library
import "react-phone-input-2/lib/style.css"; // Import the styles
import ReCAPTCHA from "react-google-recaptcha";
import OTPInput from "../../components/auth/OTPInput"; // Import OTP input component
import "./whatsapp-auth.css"; // WhatsApp themed CSS
import video from "../../assets/gif/emailVerification.mp4";

const { Title, Text } = Typography;

const SignupWith2FA = () => {
  const { companyData } = useCompany();
  const { theme } = useOutletContext() || { theme: 'light' }; // Get theme from AuthLayout
  // Existing state variables
  const [form] = Form.useForm();
  const [step, setStep] = useState(1);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [step1Data, setStep1Data] = useState(null);
  const [step2Data, setStep2Data] = useState(null);
  const [otpSentVia, setOtpSentVia] = useState("email");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [contactMethod, setContactMethod] = useState("phone");
  const [otpValue, setOtpValue] = useState(""); // Add OTP state

  // Google reCAPTCHA state
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = React.useRef(null);
  const navigate = useNavigate();
  const [slideClass, setSlideClass] = useState('');

  // Slide direction on mount
  useEffect(() => {
    const dir = sessionStorage.getItem('auth-slide');
    if (dir) {
      setSlideClass(dir === 'right' ? 'slide-from-right' : 'slide-from-left');
      sessionStorage.removeItem('auth-slide');
    }
  }, []);

  const goToLogin = () => {
    sessionStorage.setItem('auth-slide', 'left');
    navigate('/login');
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle reCAPTCHA verification
  const handleCaptchaChange = (token) => {
    setCaptchaVerified(!!token);
    setCaptchaError("");
  };

  // Handle reCAPTCHA expiry
  const handleCaptchaExpired = () => {
    setCaptchaVerified(false);
  };

  // Handle OTP input change
  const handleOTPChange = (value) => {
    setOtpValue(value);
    form.setFieldsValue({ otp: value });
    // Only validate if we have a complete OTP
    if (value.length === 6) {
      form.validateFields(['otp']);
    }
  };

  // Handle contact method change (email or phone)
  const handleContactMethodChange = (e) => {
    // Existing code...
    const method = e.target.value;
    setContactMethod(method);

    // Clear the other field when switching
    if (method === "email") {
      setPhoneNumber("");
      form.setFieldsValue({ mobile: "", countrycode: "" });
    } else {
      form.setFieldsValue({ email: "" });
    }

    // Also clear validation errors
    form.validateFields(method === "email" ? ["email"] : ["mobile"]);
  };

  // Replace the mobile form item with PhoneInput
  const handlePhoneChange = (value, country) => {
    // Existing code...
    setPhoneNumber(value);
    const code = `+${country.dialCode}`;
    setCountryCode(code);

    form.setFieldsValue({
      mobile: value.replace(code, ""),
      countrycode: code,
    });
  };

  // Handle user info submission (modified to include custom captcha)
  const handleUserInfoSubmit = async (values) => {
    // Verify reCAPTCHA first
    if (!captchaVerified) {
      setCaptchaError("Please complete the reCAPTCHA verification");
      return;
    }

    // Create the request body based on contact method
    const requestBody = {
      companyname: values.companyname,
      contactname: values.contactname,
      password: values.password,
      confirmpassword: values.confirmpassword,
    };

    // Add either email or mobile+countrycode based on contact method
    if (contactMethod === "email") {
      requestBody.email = values.email;
    } else {
      requestBody.mobile = values.mobile;
      requestBody.countrycode = values.countrycode.startsWith("+")
        ? values.countrycode
        : `+${values.countrycode}`;
    }

    console.log("User Info Submitted:", requestBody);
    await sendSignUpOTP(requestBody);
  };

  const sendSignUpOTP = (values) => {
    // api for sending otp code to user
    // removing countrycode from mobile no
    const isPhone = contactMethod === "phone";
    const isEmail = contactMethod === "email";
    let reqbody = {};
    if (isPhone) {
      const moblilewithccp = values.mobile;
      const localNumber = moblilewithccp.startsWith(
        countryCode.replace("+", "")
      )
        ? moblilewithccp.slice(countryCode.replace("+", "").length)
        : moblilewithccp;

      reqbody = {
        companyname: values.companyname,
        contactname: values.contactname,
        mobile: localNumber,
        countrycode: values.countrycode.replace("+", ""),
        password: values.password,
        confirmpassword: values.confirmpassword,
      };
    } else if (isEmail) {
      reqbody = {
        companyname: values.companyname,
        contactname: values.contactname,
        email: values.email,
        password: values.password,
        confirmpassword: values.confirmpassword,
      };
    }

    setLoading(true);
    axiosconfig
      .post("/company/sendsignupotp", reqbody)
      .then((response) => {
        // Existing code...
        // console.log("response=>", response.data);
        if (response.data?.statuscode === 200) {
          const msg = response.data?.entity?.otpresponse?.otpsentvia;
          // Set the OTP delivery method based on the response
          if (msg) {
            setOtpSentVia(msg);
          } else {
            // Default to email if not specified
            setOtpSentVia("");
          }

          message.success(response.data.message);
          setStep(2);
          form.resetFields();
          setStep1Data(reqbody);
        } else {
          message.error(
            response.data.message || "Signup failed. Please try again."
          );
          // Reset reCAPTCHA on failure
          setCaptchaVerified(false);
          recaptchaRef.current?.reset();
        }
      })
      .catch((error) => {
        console.error("Signup error:", error);
        message.error("An error occurred during signup. Please try again.");
        // Reset reCAPTCHA on failure
        setCaptchaVerified(false);
        recaptchaRef.current?.reset();
      })
      .finally(() => setLoading(false));
  };

  const handleOtpSubmit = (values) => {
    console.log("OTP Submit Values:", values);
    console.log("Step1Data:", step1Data);

    // taking values from step 1 form - add only what was entered
    if (step1Data.email) {
      values.email = step1Data.email;
    }
    if (step1Data.mobile) {
      values.mobile = step1Data.mobile;
      values.countrycode = step1Data.countrycode;
    }
    values.companyname = step1Data.companyname;
    values.contactname = step1Data.contactname;
    axiosconfig
      .post("/company/verifysignupotp", values)
      .then((response) => {
        console.log(response.data);
        if (response.data?.statuscode === 200) {
          message.success(response.data.message);
          form.resetFields();
          // Auto-finalize: call final signup with saved password from step 1
          const finalValues = { ...values };
          finalValues.password = step1Data.password;
          finalValues.confirmpassword = step1Data.confirmpassword;
          finalValues.companyname = step1Data.companyname;
          finalValues.contactname = step1Data.contactname;
          finalValues.otp = values.otp;
          if (companyData.hasRedirectUrl) {
            finalValues.company_type = "DIRECT_CUSTOMER";
            finalValues.parent_partner_id = null;
          } else {
            finalValues.company_type = "DIRECT_CUSTOMER";
            finalValues.parent_partner_id = companyData.customeruuid;
          }
          setLoading(true);
          axiosconfig
            .post("/company/verifysignupotpnsignup", finalValues)
            .then((res) => {
              if (res.data?.statuscode === 200) {
                message.success(res.data.message);
                navigate("/login");
              } else {
                message.error(res.data.message || "Signup failed. Please try again.");
              }
            })
            .catch(() => message.error("An error occurred. Please try again."))
            .finally(() => setLoading(false));
        } else {
          message.error(
            response.data.message || "Signup failed. Please try again."
          );
        }
      })
      .catch((error) => {
        console.error("Signup error:", error);
        message.error("An error occurred during signup. Please try again.");
      });
  };
  const handlePasswordSubmit = (values) => {
    // taking values from step 2 form - add only what was entered
    console.log("Step 2 Data:", step2Data);
    
    if (step2Data.email) {
      values.email = step2Data.email;
    }
    if (step2Data.mobile) {
      values.mobile = step2Data.mobile;
      values.countrycode = step2Data.countrycode;
    }
    values.companyname = step1Data.companyname;
    values.contactname = step1Data.contactname;
    values.otp = step2Data.otp;
    // values.password already set by form
   console.log("companyData",companyData)
    // Add new required fields based on redirectUrl presence
    if (companyData.hasRedirectUrl) {
      values.company_type = "DIRECT_CUSTOMER";
      values.parent_partner_id = null;
    } else {
      values.company_type = "DIRECT_CUSTOMER";
      values.parent_partner_id = companyData.customeruuid;
    }
    
    console.log("Final Signup Data:", values);
    setLoading(true);
    axiosconfig
      .post("/company/verifysignupotpnsignup", values)
      .then((response) => {
        console.log("response=>", response.data); 
        if (response.data?.statuscode === 200) {
          message.success(response.data.message);
          form.resetFields();
          // Redirect to login page after successful signup
          navigate("/login");
        } else {  
          message.error(
            response.data.message || "Signup failed. Please try again."
          );
        }
      })
      .catch((error) => {
        console.error("Signup error:", error);
        message.error("An error occurred during signup. Please try again.");  
      })
      .finally(() => setLoading(false));
  };


  const handleCapitalize = (e, field) => {
    const value = e.target.value;
    const formattedValue = value
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    form.setFieldsValue({ [field]: formattedValue });
  };

  // eslint-disable-next-line no-unused-vars
  const mobileValidator = (value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^([6|7|8|9]{1})[0-9]{9}$/.test(value)) {
      return Promise.reject("Mobile number is invalid!");
    }
    if (value.length !== 10) {
      return Promise.reject("Mobile number must be 10 digits!");
    }
    return Promise.resolve();
  };

  // Trigger the modal to show when user clicks Resend OTP
  // eslint-disable-next-line no-unused-vars
  const handleResendOtp = () => {
    // Just call performResendOtp directly, no captcha
    performResendOtp();
  };

  // The actual resend OTP function that will be called after captcha verification
  const performResendOtp = () => {
    let reqbody = {};
    if (step1Data.email) {
      reqbody.email = step1Data.email;
    }
    if (step1Data.mobile) {
      reqbody.mobile = step1Data.mobile;
      reqbody.countrycode = step1Data.countrycode;
    }

    setResendLoading(true);
    axiosconfig
      .post("/company/resendsignupotp", reqbody)
      .then((response) => {
        if (response.data?.statuscode === 200) {
          // Use the correct value from response
          const sentVia =
            response.data?.entity?.otpresponse?.otpsentvia ||
            response.data?.otpsentvia ||
            (step1Data.email ? "email" : "phone");
          setOtpSentVia(sentVia);

          form.setFieldValue("otp", "");
          setOtpValue(""); // Clear OTP state

          let channelMsg = "your ";
          if (sentVia?.toLowerCase() === "whatsapp") channelMsg += "WhatsApp";
          else if (sentVia?.toLowerCase() === "sms") channelMsg += "phone via SMS";
          else channelMsg += "Email";

          message.success(`OTP resent to ${channelMsg}`);
          setResendCooldown(120);
        } else {
          form.setFieldValue("otp", "");
          setOtpValue(""); // Clear OTP state
          message.error(
            response.data.message || "Signup failed. Please try again."
          );
        }
      })
      .catch((error) => {
        console.error("Signup error:", error);
        form.setFieldValue("otp", "");
        setOtpValue(""); // Clear OTP state
        message.error("An error occurred during signup. Please try again.");
      })
      .finally(() => {
        setResendLoading(false);
        setLoading(false);
      });
  };

  // Cooldown timer effect
  React.useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  return (
    <div className="wa-auth-container" data-theme={theme}>

      {/* ── Animated background ── */}
      <div className="wa-auth-bg-anim" aria-hidden="true">
        <div className="wa-bg-bubble-shape s1"></div>
        <div className="wa-bg-bubble-shape s2"></div>
        <div className="wa-bg-bubble-shape s3"></div>
        <div className="wa-bg-bubble-shape s4"></div>
        <div className="wa-bg-bubble-shape s5"></div>
        <div className="wa-bg-bubble-shape s6"></div>
        <div className="wa-bg-dot d1"></div>
        <div className="wa-bg-dot d2"></div>
        <div className="wa-bg-dot d3"></div>
        <div className="wa-bg-dot d4"></div>
        <div className="wa-bg-dot d5"></div>
        <div className="wa-bg-orb-light o1"></div>
        <div className="wa-bg-orb-light o2"></div>
        <div className="wa-bg-orb-light o3"></div>
        <div className="wa-bg-wave-bars">
          <div className="wa-bg-wave-bar"></div>
          <div className="wa-bg-wave-bar"></div>
          <div className="wa-bg-wave-bar"></div>
          <div className="wa-bg-wave-bar"></div>
          <div className="wa-bg-wave-bar"></div>
          <div className="wa-bg-wave-bar"></div>
          <div className="wa-bg-wave-bar"></div>
          <div className="wa-bg-wave-bar"></div>
        </div>
      </div>

      {/* ── Form card ── */}
      <div className={`wa-auth-split-card wa-auth-card--signup ${slideClass}`}>
        <div className="wa-auth-form-side">
        <div className="wa-auth-form-container">

        <div className="wa-auth-header">
          <Title level={2} className="wa-auth-title">
            {step === 1
              ? "Create your account"
              : step === 2
              ? "Verify OTP"
              : "Set Password"}
          </Title>

          {/* Existing header content... */}
          {step === 1 ? (
            <Text type="secondary" className="wa-auth-subtitle">Fill in the details below to get started.</Text>
          ) : step === 2 ? (
            <div className="wa-auth-subtitle">
              <Text
                strong
                style={{
                  fontSize: "15px",
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                Enter the 6-digit OTP
              </Text>
              <Flex align="center" justify="center" gap="small">
                {otpSentVia?.toLowerCase() === "whatsapp" ? (
                  <>
                    <WhatsAppOutlined style={{ fontSize: "16px", color: "#25D366" }} />
                    <Text type="secondary">
                      Sent to your{" "}
                      <Text strong style={{ color: "#128C7E" }}>
                        WhatsApp
                      </Text>
                    </Text>
                  </>
                ) : otpSentVia?.toLowerCase() === "sms" ? (
                  <>
                    <span
                      role="img"
                      aria-label="sms"
                      style={{ fontSize: "16px" }}
                    >
                      💬
                    </span>{" "}
                    <Text type="secondary">
                      Sent to your{" "}
                      <Text strong style={{ color: "#128C7E" }}>
                        phone via SMS
                      </Text>
                    </Text>
                  </>
                ) : (
                  <>
                    <MailOutlined style={{ color: "#128C7E" }} />{" "}
                    <Text type="secondary">
                      Sent to your{" "}
                      <Text strong style={{ color: "#128C7E" }}>
                        Email
                      </Text>
                    </Text>
                  </>
                )}
              </Flex>
            </div>
          ) : (
            <Text type="secondary" className="wa-auth-subtitle">Set your password</Text>
          )}
        </div>

        <Form
          form={form}
          layout="vertical"
          disabled={loading}
          onFinish={
            step === 1
              ? handleUserInfoSubmit
              : handleOtpSubmit
          }
        >
          {step === 1 && (
            <>
              {/* Existing form items... */}
              <Form.Item
                name="companyname"
                label="Company Name"
                rules={[
                  { required: true, message: ' Please enter your company name' },
                  { min: 4, message: ' Company name must be at least 4 characters' },
                  { max: 30, message: ' Company name cannot exceed 30 characters' },
                  {
                    pattern: /^[a-zA-Z][a-zA-Z0-9\s]*[a-zA-Z0-9]+$/,
                    message: ' Company name must start with a letter'
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      
                      // Check if it's only spaces
                      if (value.trim().length === 0) {
                        return Promise.reject(new Error(' Company name cannot consist of only spaces'));
                      }
                      
                      // Check if it has at least 2 non-space characters
                      const nonSpaceChars = value.replace(/\s/g, '').length;
                      if (nonSpaceChars < 2) {
                        return Promise.reject(new Error(' Company name must have at least 2 non-space characters'));
                      }
                      
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  prefix={<BankOutlined className="wa-input-prefix" />}
                  placeholder="Enter Company Name"
                  className="wa-input"
                  onBeforeInput={handleBeforeInput(
                    changevalidations.letterwithspace,
                    30
                  )}
                  onChange={(e) => handleCapitalize(e, "companyname")}
                  onBlur={(e) => {
                    // Trim leading and trailing spaces on blur
                    const trimmedValue = e.target.value.trim();
                    if (trimmedValue !== e.target.value) {
                      form.setFieldsValue({ companyname: trimmedValue });
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                name="contactname"
                label="Contact Name"
                rules={[
                  { required: true, message: ' Please enter your contact name' },
                  { min: 4, message: ' Contact name must be at least 4 characters' },
                  { max: 30, message: ' Contact name cannot exceed 30 characters' },
                  {
                    pattern: /^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]+$/,
                    message: ' Contact name must contain only letters'
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      
                      // Check if it's only spaces
                      if (value.trim().length === 0) {
                        return Promise.reject(new Error(' Contact name cannot consist of only spaces'));
                      }
                      
                      // Check if it has at least 2 non-space characters
                      const nonSpaceChars = value.replace(/\s/g, '').length;
                      if (nonSpaceChars < 2) {
                        return Promise.reject(new Error(' Contact name must have at least 2 non-space characters'));
                      }
                      
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  prefix={<UserOutlined className="wa-input-prefix" />}
                  placeholder="Enter Contact Name"
                  className="wa-input"
                  onBeforeInput={handleBeforeInput(
                    changevalidations.letterwithspace,
                    30
                  )}
                  onChange={(e) => handleCapitalize(e, "contactname")}
                  onBlur={(e) => {
                    // Trim leading and trailing spaces on blur
                    const trimmedValue = e.target.value.trim();
                    if (trimmedValue !== e.target.value) {
                      form.setFieldsValue({ contactname: trimmedValue });
                    }
                  }}
                />
              </Form.Item>

              {/* Contact method selector */}
              <Form.Item
              // label="Preferred Contact Method"
              >
                <Radio.Group
                  onChange={handleContactMethodChange}
                  value={contactMethod}
                >
                  <Radio value="phone">Mobile</Radio>
                  <Radio value="email">Email</Radio>
                </Radio.Group>
              </Form.Item>

              {/* Conditionally show either email or phone input based on selection */}
              {contactMethod === "email" ? (
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: " Please enter your email" },
                    { type: "email", message: " Invalid email" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined className="wa-input-prefix" />}
                    placeholder="Enter Email"
                    className="wa-input"
                    onBeforeInput={handleBeforeInput(
                      changevalidations.allwithoutspace,
                      50
                    )}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="mobile"
                  label="Mobile Number"
                  rules={[
                    { required: true, message: " Please enter a mobile number" },
                  ]}
                >
                  <PhoneInput
                    country={"in"}
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    inputClass="wa-input"
                    containerClass="wa-phone-input"
                    dropdownClass="wa-phone-dropdown"
                    enableSearch={true}
                    disableSearchIcon={true}
                    searchPlaceholder="Search country..."
                    countryCodeEditable={false}
                  />
                </Form.Item>
              )}

              {/* Hidden field to store country code */}
              <Form.Item name="countrycode" hidden initialValue="+91" />

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: " Please enter your password" },
                  { min: 8, message: " Minimum 8 characters" },
                  { pattern: /[A-Z]/, message: " At least one uppercase letter" },
                  { pattern: /[a-z]/, message: " At least one lowercase letter" },
                  { pattern: /[0-9]/, message: " At least one number" },
                  { pattern: /[^A-Za-z0-9]/, message: " At least one special character" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="wa-input-prefix" />}
                  placeholder="Create Password"
                  className="wa-input"
                />
              </Form.Item>

              <Form.Item
                name="confirmpassword"
                label="Confirm Password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: " Confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(" Passwords do not match!");
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="wa-input-prefix" />}
                  placeholder="Confirm Password"
                  className="wa-input"
                />
              </Form.Item>

              <Form.Item
                className="wa-captcha-wrapper"
              >
                <div className="wa-recaptcha-wrap">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    size="normal"
                  />
                </div>
                {captchaError && (
                  <Text type="danger" style={{ display: 'block', textAlign: 'center', marginTop: 4 }}>{captchaError}</Text>
                )}
              </Form.Item>

              {/* Submit button */}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="wa-auth-button"
                  loading={loading}
                  block
                  icon={<CheckOutlined />}
                  style={{
                    borderRadius: 12,
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: 48,
                  }}
                >
                  Create Account
                </Button>
              </Form.Item>
            </>
          )}

          {step === 2 && (
            <>
              <Flex justify="center">
                <video
                  src={video}
                  alt="No Data Found"
                  style={{ width: "100px", height: "100px" }}
                  autoPlay
                  loop
                  className="mb-2"
                />
              </Flex>
              
              <Form.Item
                name="otp"
                label="Enter 6-digit OTP"
                rules={[
                  { required: true, message: " Please enter the complete OTP" },
                  { pattern: /^\d{6}$/, message: " OTP must be exactly 6 digits" }
                ]}
                className="wa-otp-form-item"
              >
                <OTPInput
                  length={6}
                  value={otpValue}
                  onChange={handleOTPChange}
                />
              </Form.Item>
              
              <Form.Item>
                <Button
                  type="link"
                  onClick={performResendOtp}
                  disabled={resendLoading || resendCooldown > 0}
                  loading={resendLoading}
                  block
                  style={{ padding: 0 }}
                >
                  {resendCooldown > 0
                    ? `Resend OTP (${resendCooldown}s)`
                    : "Resend OTP"}
                </Button>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="wa-auth-button"
                  loading={loading}
                  block
                  icon={<ArrowRightOutlined />}
                  style={{
                    borderRadius: 12,
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: 48,
                  }}
                >
                  Next
                </Button>
              </Form.Item>
            </>
          )}

          {step === 3 && (
            <>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: " Please enter your password" },
                  { min: 8, message: " Minimum 8 characters" },
                  { pattern: /[A-Z]/, message: " At least one uppercase letter" },
                  { pattern: /[a-z]/, message: " At least one lowercase letter" },
                  { pattern: /[0-9]/, message: " At least one number" },
                  { pattern: /[^A-Za-z0-9]/, message: " At least one special character" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="wa-input-prefix" />}
                  placeholder="Password"
                  className="wa-input"
                />
              </Form.Item>
              <Form.Item
                name="confirmpassword"
                label="Confirm Password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: " Confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(" Passwords do not match!");
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="wa-input-prefix" />}
                  placeholder="Confirm Password"
                  className="wa-input"
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="wa-auth-button"
                  loading={loading}
                  block
                  icon={<CheckOutlined />}
                  style={{
                    borderRadius: 12,
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: 48,
                  }}
                >
                  Submit
                </Button>
              </Form.Item>
            </>
          )}
        </Form>

        <div className="wa-auth-footer">
          {step === 1 ? (
            <Text type="secondary">
              Already have an account?{" "}
              <button type="button" className="wa-auth-link" onClick={goToLogin}>
                Log In
              </button>
            </Text>
          ) : (
            <a
              className="wa-auth-link"
              onClick={() => {
                if (step === 2) {
                  setStep(1);
                  form.setFieldsValue({
                    companyname: step1Data?.companyname,
                    email: step1Data?.email,
                    mobile: step1Data?.mobile,
                    contactname: step1Data?.contactname,
                  });
                } else if (step === 3) {
                  setStep(2);
                  form.setFieldsValue({
                    companyname: step1Data?.companyname,
                    email: step1Data?.email,
                    mobile: step1Data?.mobile,
                    contactname: step1Data?.contactname,
                  });
                }
              }}
            >
              ← Back to sign up
            </a>
          )}
        </div>
      </div>
        </div>{/* end wa-auth-form-side */}

      </div>{/* end wa-auth-split-card */}
    </div>
  );
};

export default SignupWith2FA;
